/**
 * StopsImportDialog — import Excel du référentiel Arrêts / POI MBC.
 * Étapes : fichier → analyse (feuilles/colonnes réelles) → mapping confirmé →
 * prévisualisation classée NEW / EXACT_DUPLICATE / POSSIBLE_CONFLICT / INVALID → import.
 */
import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ActionButton, StatusBadge } from "@/components/ds";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, Upload } from "lucide-react";

const FIELD = "w-full border border-zinc-900 px-2 py-1.5 text-sm bg-white";
const LABEL = "block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1";
const STATUSES = ["NEW", "EXACT_DUPLICATE", "SAME_COORDS_DIFF_NAME", "SAME_NAME_DIFF_COORDS", "INVALID"];
const STATUS_TONE = { NEW: "success", EXACT_DUPLICATE: "neutral", SAME_COORDS_DIFF_NAME: "info", SAME_NAME_DIFF_COORDS: "warning", INVALID: "danger" };
const STATUS_LABEL = { NEW: "Nouveau", EXACT_DUPLICATE: "Doublon exact", SAME_COORDS_DIFF_NAME: "Coordonnées partagées", SAME_NAME_DIFF_COORDS: "Conflit à valider", INVALID: "Invalide" };
const STATUS_ACTION = {
  NEW: "→ importé actif", EXACT_DUPLICATE: "→ pas de doublon créé",
  SAME_COORDS_DIFF_NAME: "→ les deux conservés (IDs distincts), signalés « coordonnées partagées »",
  SAME_NAME_DIFF_COORDS: "→ un seul enregistrement inactif avec les 2 coordonnées à valider dans POI MBC",
  INVALID: "→ non importé",
};

function Preview({ preview, filter, setFilter }) {
  const rows = preview.rows.filter((r) => filter === "ALL" || r.status === filter);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1" data-testid="import-counts">
        {["ALL", ...STATUSES].map((k) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`import-filter-${k}`}
            className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-zinc-900 ${filter === k ? "bg-zinc-900 text-[#FFC60A]" : "bg-white"}`}>
            {k === "ALL" ? `Tout ${preview.row_count}` : `${STATUS_LABEL[k]} ${preview.counts[k]}`}
          </button>
        ))}
      </div>
      <div className="max-h-[300px] overflow-auto border border-zinc-300">
        <table className="w-full text-xs">
          <thead className="bg-zinc-900 text-white sticky top-0">
            <tr>
              <th className="px-2 py-1 text-left font-mono text-[10px]">L.</th>
              <th className="px-2 py-1 text-left font-mono text-[10px]">Nom</th>
              <th className="px-2 py-1 text-left font-mono text-[10px]">GPS</th>
              <th className="px-2 py-1 text-left font-mono text-[10px]">Statut</th>
              <th className="px-2 py-1 text-left font-mono text-[10px]">Détail / décision</th>
            </tr>
          </thead>
          <tbody data-testid="import-preview-body">
            {rows.map((r) => (
              <tr key={r.row} className="border-t border-zinc-200" data-testid={`import-row-${r.row}`}>
                <td className="px-2 py-1 font-mono text-zinc-500">{r.row}</td>
                <td className="px-2 py-1 font-medium">{r.name || <em className="text-zinc-400">—</em>}</td>
                <td className="px-2 py-1 font-mono text-zinc-600">{r.latitude != null ? `${r.latitude}, ${r.longitude}` : "—"}</td>
                <td className="px-2 py-1"><StatusBadge tone={STATUS_TONE[r.status]} label={STATUS_LABEL[r.status]} size="sm" /></td>
                <td className="px-2 py-1 text-zinc-600">
                  {r.reason ? <>{r.reason} </> : null}
                  <span className="text-zinc-900 font-medium">{STATUS_ACTION[r.status]}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function StopsImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [sheet, setSheet] = useState("");
  const [mapping, setMapping] = useState({ name_col: null, gps_col: null, lat_col: null, lng_col: null });
  const [preview, setPreview] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setAnalysis(null); setSheet(""); setPreview(null); setResult(null); setFilter("ALL"); };
  const close = () => { reset(); onClose(); };
  const sheetInfo = useMemo(() => analysis?.sheets.find((s) => s.name === sheet), [analysis, sheet]);
  const mode = mapping.gps_col != null || (mapping.lat_col == null && mapping.lng_col == null) ? "gps" : "latlng";

  const fd = () => {
    const f = new FormData();
    f.append("file", file); f.append("sheet", sheet); f.append("mapping", JSON.stringify(mapping));
    return f;
  };

  const analyze = async (f) => {
    setFile(f); setPreview(null); setResult(null);
    const form = new FormData(); form.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post("/stops/import/analyze", form);
      setAnalysis(data);
      const first = data.sheets.find((s) => s.row_count > 0) || data.sheets[0];
      setSheet(first?.name || "");
      setMapping({ name_col: null, gps_col: null, lat_col: null, lng_col: null, ...(first?.suggested_mapping || {}) });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); setAnalysis(null); }
    finally { setBusy(false); }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stops/import/preview", fd());
      setPreview(data); setFilter("ALL");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stops/import/apply", fd());
      setResult(data); toast.success(`${data.created} arrêt(s) importé(s)`);
      onImported && onImported();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const colSelect = (k, testId, allowNone = false) => (
    <select data-testid={testId} className={FIELD} value={mapping[k] ?? ""} onChange={(e) => setMapping((m) => ({ ...m, [k]: e.target.value === "" ? null : Number(e.target.value) }))}>
      <option value="">{allowNone ? "— aucune —" : "— choisir —"}</option>
      {(sheetInfo?.headers || []).map((h, i) => <option key={i} value={i}>{h}</option>)}
    </select>
  );
  const toImport = preview ? preview.counts.NEW + preview.counts.SAME_COORDS_DIFF_NAME + preview.counts.SAME_NAME_DIFF_COORDS : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-4xl border border-zinc-900 rounded-none p-0" data-testid="stops-import-dialog">
        <DialogHeader className="bg-zinc-900 text-white px-4 py-3">
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <FileSpreadsheet size={16} className="text-[#FFC60A]" /> Importer des arrêts depuis Excel
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Lecture réelle du fichier, mapping confirmé par vous, puis classification avant toute écriture. Aucune fusion automatique.
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 space-y-4">
          <div>
            <label className={LABEL}>1 · Fichier .xlsx</label>
            <input type="file" accept=".xlsx" data-testid="import-file-input" disabled={busy}
              onChange={(e) => e.target.files?.[0] && analyze(e.target.files[0])}
              className="text-sm" />
          </div>

          {analysis && (
            <div className="border border-zinc-300 p-3 space-y-3 bg-zinc-50" data-testid="import-analysis">
              <div className="text-xs">
                <span className="font-mono uppercase tracking-widest text-zinc-500">Fichier</span> <b>{analysis.filename}</b>
                {" · "}<span className="font-mono uppercase tracking-widest text-zinc-500">Feuilles</span> {analysis.sheets.map((s) => `${s.name} (${s.row_count} lignes)`).join(", ")}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <label className={LABEL}>2 · Feuille</label>
                  <select data-testid="import-sheet" className={FIELD} value={sheet} onChange={(e) => { setSheet(e.target.value); const si = analysis.sheets.find((s) => s.name === e.target.value); setMapping({ name_col: null, gps_col: null, lat_col: null, lng_col: null, ...(si?.suggested_mapping || {}) }); setPreview(null); }}>
                    {analysis.sheets.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
                <div><label className={LABEL}>Colonne Nom *</label>{colSelect("name_col", "import-map-name")}</div>
                {mode === "gps" ? (
                  <div><label className={LABEL}>Colonne « lat, lon » *</label>{colSelect("gps_col", "import-map-gps")}</div>
                ) : (
                  <>
                    <div><label className={LABEL}>Colonne Latitude *</label>{colSelect("lat_col", "import-map-lat")}</div>
                    <div><label className={LABEL}>Colonne Longitude *</label>{colSelect("lng_col", "import-map-lng")}</div>
                  </>
                )}
                <div className="flex items-end">
                  <button type="button" data-testid="import-toggle-mode" className="text-[10px] font-mono uppercase tracking-widest underline text-zinc-600"
                    onClick={() => setMapping((m) => (mode === "gps" ? { ...m, gps_col: null, lat_col: m.lat_col ?? 0, lng_col: m.lng_col ?? 0 } : { ...m, lat_col: null, lng_col: null, gps_col: 0 }))}>
                    {mode === "gps" ? "Lat / Lon séparées ?" : "Colonne GPS combinée ?"}
                  </button>
                </div>
              </div>
              {sheetInfo?.sample_rows?.length > 0 && (
                <div className="text-[11px] font-mono text-zinc-600 overflow-auto">
                  <div className="uppercase tracking-widest text-[10px] text-zinc-500 mb-1">Aperçu des premières lignes</div>
                  {sheetInfo.sample_rows.map((r, i) => <div key={i}>{r.join("  |  ")}</div>)}
                </div>
              )}
              <ActionButton variant="dark" onClick={runPreview} loading={busy} testId="import-preview-btn"
                disabled={mapping.name_col == null || (mode === "gps" ? mapping.gps_col == null : (mapping.lat_col == null || mapping.lng_col == null))}>
                3 · Analyser les lignes
              </ActionButton>
            </div>
          )}

          {preview && !result && <Preview preview={preview} filter={filter} setFilter={setFilter} />}

          {result && (
            <div className="border border-emerald-600 bg-emerald-50 p-3 text-sm space-y-2" data-testid="import-result">
              <div>
                Lignes lues <b>{result.rows_read}</b> · POI importés <b data-testid="import-result-created">{result.created}</b> ·
                doublons exacts ignorés <b>{result.counts_before.EXACT_DUPLICATE}</b> ·
                coordonnées partagées <b>{result.shared_coords.length}</b> ·
                conflits à valider <b data-testid="import-result-conflicts">{result.conflicts.length}</b> ·
                invalides <b>{result.counts_before.INVALID}</b> · coordonnée choisie automatiquement : <b>NON</b>
              </div>
              {result.conflicts.length > 0 && (
                <table className="w-full text-xs border border-orange-300 bg-white">
                  <thead className="bg-orange-100"><tr><th className="px-2 py-1 text-left">Conflit à valider</th><th className="px-2 py-1 text-left">Coordonnées A</th><th className="px-2 py-1 text-left">Coordonnées B</th></tr></thead>
                  <tbody>
                    {result.conflicts.map((c) => (
                      <tr key={c.stop_id} className="border-t border-orange-200">
                        <td className="px-2 py-1 font-medium">{c.name}</td>
                        <td className="px-2 py-1 font-mono">{c.coords_a.latitude}, {c.coords_a.longitude} <span className="text-zinc-500">(L{c.coords_a.row} {Object.values(c.coords_a.extra || {}).join(" ")})</span></td>
                        <td className="px-2 py-1 font-mono">{c.coords_b.latitude}, {c.coords_b.longitude} <span className="text-zinc-500">(L{c.coords_b.row} {Object.values(c.coords_b.extra || {}).join(" ")})</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="text-xs text-zinc-600">Les conflits sont inactifs jusqu'à validation de la coordonnée dans la page POI MBC (« Résoudre »).</div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <ActionButton variant="ghost" onClick={close} testId="import-close">{result ? "Fermer" : "Annuler"}</ActionButton>
            {preview && !result && (
              <ActionButton variant="primary" icon={Upload} onClick={apply} loading={busy} disabled={toImport === 0} testId="import-apply-btn">
                4 · Importer {toImport} arrêt(s)
              </ActionButton>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
